import React, { useState } from 'react';
import { modelRegistryAPI } from '../api';

// Colors per verdict: green = trustworthy signal, amber = borderline, red = broken.
const VERDICT_STYLE = {
  strong: { bg: '#e7f5ec', border: '#2e7d32', text: '#1b5e20', tag: 'Strong' },
  moderate: { bg: '#eef4fb', border: '#1565c0', text: '#0d47a1', tag: 'Moderate' },
  weak: { bg: '#fdf5e6', border: '#ef6c00', text: '#a15200', tag: 'Weak' },
  no_signal: { bg: '#f4f4f4', border: '#757575', text: '#424242', tag: 'No signal' },
  inverted: { bg: '#fdecef', border: '#b00020', text: '#8c001a', tag: 'Inverted' },
  insufficient_data: { bg: '#f4f4f4', border: '#757575', text: '#424242', tag: 'Not enough data' },
};

// Readable names for the control cohorts. Unknown ids fall back to their raw id.
const COHORT_LABEL = {
  control_other_nd_v1: 'Other neurodegeneration (PD / FTD)',
  control_shared_v1: 'Shared across AD, PD and FTD',
  neuro_control_v1: 'Other neurological disease',
  inflammation_control_v1: 'General inflammation',
  aging_control_v1: 'Aging',
  random_control_v1: 'Random background',
};

// Small self-contained panel: runs the independent AD-association benchmark for a
// saved model and shows the key held-out metrics. Keeps its own state so it does
// not touch the rest of ModelRegistry.
function AdBenchmarkPanel({ modelId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await modelRegistryAPI.runAdBenchmark(modelId);
      setResult(res);
    } catch (e) {
      setError(e?.message || 'Benchmark failed');
    } finally {
      setLoading(false);
    }
  };

  const report = result?.primary_independent_benchmark;
  const primary = report?.primary_ad_vs_pooled_controls;
  const perCohort = Object.entries(report?.per_control_cohort || {});
  const coverage = report?.coverage;
  const independence = result?.independence;
  const reading = report?.interpretation;
  const style = VERDICT_STYLE[reading?.verdict] || VERDICT_STYLE.insufficient_data;
  // Diagnostic mode is the backend default while the holdout is under investigation:
  // every cohort gene is scored, so nothing here is held out and the labels must say so.
  const noHoldout = independence?.mode === 'no_holdout_diagnostic';

  const fmt = (v, d = 3) => (v === null || v === undefined ? '—' : Number(v).toFixed(d));
  const ci = (arr) => (Array.isArray(arr) ? `95% CI [${fmt(arr[0])}, ${fmt(arr[1])}]` : '');

  return (
    <div className="model-registry__ad-benchmark" style={{ marginTop: 16 }}>
      <h4>AD benchmark{noHoldout ? ' (diagnostic — no holdout)' : ' (held-out)'}</h4>
      <p style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
        Checks whether the model scores known Alzheimer&apos;s genes higher than unrelated
        control genes (other neurological disease, inflammation, aging, random background).
      </p>
      {noHoldout && (
        <p style={{ fontSize: 12, color: '#8a5a00', background: '#fff8e1', border: '1px solid #ffe082',
                    borderRadius: 4, padding: '6px 8px', marginBottom: 8 }}>
          Diagnostic run: leakage exclusion is off and every cohort gene is scored,
          including genes the model trained on. These numbers are for debugging the
          benchmark harness — they are not an independent performance estimate.
        </p>
      )}
      <button type="button" className="btn btn--secondary" onClick={run} disabled={loading || !modelId}>
        {loading ? 'Running…' : 'Run AD Benchmark'}
      </button>

      {error && <p style={{ color: '#b00020', fontSize: 13, marginTop: 8 }}>{error}</p>}

      {reading && (
        <div style={{ marginTop: 12 }}>
          {/* Verdict first: the one-line answer, before any numbers. */}
          <div
            style={{
              background: style.bg,
              borderLeft: `4px solid ${style.border}`,
              padding: '10px 12px',
              borderRadius: 4,
            }}
          >
            <div style={{ fontWeight: 600, color: style.text, fontSize: 14 }}>
              {style.tag}
              {reading.confidence === 'provisional' && (
                <span style={{ fontWeight: 400, fontStyle: 'italic' }}> (provisional)</span>
              )}{' '}
              — {reading.headline}
            </div>
            <div style={{ fontSize: 13, color: '#444', marginTop: 4 }}>{reading.what_it_means}</div>
          </div>

          {/* The direction assumption flips the verdict outright, so it is stated up
              front rather than buried in the details. */}
          {result.higher_is_more_ad !== undefined && (
            <div style={{ fontSize: 12, color: '#555', marginTop: 8 }}>
              Assumed scoring direction on <code>{result.target_column}</code>:{' '}
              <strong>
                {result.higher_is_more_ad
                  ? 'higher predicted value = more AD-associated'
                  : 'lower predicted value = more AD-associated'}
              </strong>{' '}
              ({result.orientation_source === 'caller_supplied' ? 'set explicitly' : 'inferred from the target name'}).
              If that is backwards for this target, the result inverts — an AUROC of {fmt(primary?.auroc, 2)}{' '}
              becomes {fmt(1 - (primary?.auroc ?? 0), 2)}.
            </div>
          )}

          {reading.caveats?.length > 0 && (
            <ul style={{ fontSize: 12, color: '#a15200', margin: '8px 0 0', paddingLeft: 18 }}>
              {reading.caveats.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          )}

          {/* Each metric with its plain-English reading right beneath the number. */}
          {reading.metric_readings?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {reading.metric_readings.map((m) => (
                <div key={m.metric} style={{ padding: '6px 0', borderTop: '1px solid #eee' }}>
                  <div style={{ fontSize: 13 }}>
                    <strong>{m.metric}</strong>{' '}
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(m.value)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{m.reading}</div>
                </div>
              ))}
            </div>
          )}

          {/* Which kind of control the model fails on is the most useful diagnostic
              here, so each control set gets its own AUROC rather than only the pool. */}
          {perCohort.length > 1 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Against each control set</div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                Beating the harder controls is what shows the model found Alzheimer&apos;s
                specifically, rather than disease genes in general.
              </div>
              {perCohort.map(([cid, m]) => (
                <div
                  key={cid}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '5px 0',
                    borderTop: '1px solid #eee',
                    fontSize: 13,
                  }}
                >
                  <span>{COHORT_LABEL[cid] || cid}</span>
                  <span style={{ color: '#888', flexShrink: 0 }}>
                    <span style={{ color: '#222', fontVariantNumeric: 'tabular-nums' }}>
                      AUROC {fmt(m.auroc)}
                    </span>{' '}
                    · n={m.n_negative}
                  </span>
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={() => setShowDetails((s) => !s)}
            style={{
              marginTop: 10,
              background: 'none',
              border: 'none',
              padding: 0,
              color: '#1565c0',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {showDetails ? 'Hide details' : 'Show details'}
          </button>

          {showDetails && (
            <div style={{ fontSize: 12, color: '#555', marginTop: 8, lineHeight: 1.6 }}>
              {primary && (
                <div>
                  AUROC {fmt(primary.auroc)} {ci(primary.auroc_ci95)} · Cohen&apos;s d{' '}
                  {fmt(primary.cohens_d)} {ci(primary.cohens_d_ci95)} · Mann–Whitney p{' '}
                  {primary.mann_whitney_pvalue_two_sided === null ||
                  primary.mann_whitney_pvalue_two_sided === undefined
                    ? '—'
                    : Number(primary.mann_whitney_pvalue_two_sided).toExponential(2)}
                </div>
              )}
              <div>
                {noHoldout ? 'AD genes scored' : 'Held-out AD genes scored'}:{' '}
                {coverage?.n_ad_validation_present ?? 0} of{' '}
                {coverage?.n_ad_validation_total ?? 0} · control genes:{' '}
                {primary?.n_negative ?? 0}
              </div>
              <div>
                Direction: {result.higher_is_more_ad ? 'higher score = more AD' : 'lower score = more AD'}
                {noHoldout
                  ? ' · leakage exclusion: off'
                  : (independence ? ` · leakage genes excluded: ${independence.leakage_gene_count}` : '')}
              </div>
              {noHoldout && result.diagnostics && (
                <div>
                  Cohort genes scored: {result.diagnostics.n_cohort_genes_scored ?? 0} · score range:{' '}
                  {fmt(result.diagnostics.score_min)} to {fmt(result.diagnostics.score_max)} ·
                  probabilities: {String(result.diagnostics.scores_are_probabilities)}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdBenchmarkPanel;
